'use client';

import {
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { IoClose } from 'react-icons/io5';
import { MdSearch } from 'react-icons/md';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useAnchoredDropdownPosition } from './useAnchoredDropdownPosition';
import { Input } from '@/components/ui/input';
import {
  menuListClasses,
  menuListEmptyStateClasses,
  menuListOptionButtonClasses,
  menuListMobileBackdropClasses,
  menuListMobileDialogClasses,
  menuListMobileHeaderClasses,
  menuListMobileTitleClasses,
  menuListMobileCloseButtonClasses,
  menuListMobileSearchClasses,
  menuListMobileBodyClasses,
  menuListDesktopOverlayClasses,
  menuListDesktopDropdownClasses,
} from './MenuList.style';

export interface MenuListOption {
  value: string;
  label: string;
  /** When true, the option is rendered greyed-out and cannot be selected. */
  disabled?: boolean;
  /** Native tooltip (title attribute) — e.g. why a disabled option is unavailable. */
  title?: string;
}

/**
 * Portal-based list of options used by the `Select` component.
 *
 * - On desktop, renders a positioned dropdown anchored to the trigger.
 * - On mobile, renders a full-width bottom sheet dialog with its own header.
 *
 * The parent controls visibility via `open` and reacts to user actions via
 * `onSelect` (option chosen) and `onClose` (dismissed).
 */
export interface MenuListProps {
  /** Whether the menu is currently visible. */
  open: boolean;

  /** Called whenever the menu should close (Escape, click-outside, selection, etc.). */
  onClose: () => void;

  /** Options rendered inside the listbox. */
  options: MenuListOption[];

  /** Called with the selected value when an option is chosen. */
  onSelect: (value: string) => void;

  /** Optional title used for labelling the list / mobile dialog. */
  title?: string;

  /** Anchor element used to position the desktop dropdown under the trigger. */
  anchorRef?: React.RefObject<HTMLElement | null>;

  /**
   * When true, render the anchored dropdown (positioned under the trigger) on mobile too
   * instead of the bottom-sheet presentation. Used by type-in-field comboboxes
   * (Autocomplete), where suggestions must appear directly below the field the user is
   * typing in rather than detached at the bottom of the screen (PB-2909). Select keeps the
   * default bottom-sheet behavior.
   */
  anchoredOnMobile?: boolean;

  /**
   * When true, size the dropdown to exactly the anchor's width so long option labels
   * wrap instead of stretching the list past its host (e.g. Google address suggestions
   * overflowing the Add Address modal, PB-2909). Off by default — narrow triggers like
   * the "more" (⋯) button rely on the dropdown being wider than the anchor.
   */
  matchAnchorWidth?: boolean;

  /** Optional id for listbox; also used for aria labelling with the trigger. */
  listboxId?: string;

  /**
   * When true on desktop, skip auto-focusing the first option so the trigger input
   * retains focus for typing to filter.
   */
  filterable?: boolean;

  /** Message shown when no options are available / match a filter. */
  noOptionsText?: string;

  /** Currently selected value, used to visually highlight the active option. */
  selectedValue?: string;

  /**
   * Multi-select selection. When provided, an option is highlighted if its value
   * is in this array (takes precedence over `selectedValue`). Used by `MultiSelect`.
   */
  selectedValues?: string[];

  /**
   * Whether picking an option closes the menu (default `true`, the single-select
   * behaviour). `MultiSelect` passes `false` so the parker can tick several
   * options in one visit.
   */
  closeOnSelect?: boolean;

  /**
   * Optional custom renderer for each option's content inside the dropdown button.
   * Receives the option data and its selected state; returns a ReactNode.
   * The button wrapper (role, aria, click, keyboard, focus styles) is still managed by MenuList.
   */
  renderOption?: (option: MenuListOption, isSelected: boolean) => ReactNode;

  /** Content rendered above the listbox. Kept outside `<ul role="listbox">` so screen readers don't announce it as an option. */
  headerSlot?: ReactNode;

  /** Override z-index when nested in a high-stacking surface — applies to the desktop dropdown + overlay and the mobile dialog + backdrop. */
  menuZIndex?: number;

  /** Optional max width (px) for the desktop dropdown. */
  maxWidth?: number;

  /** Current text for the mobile search box (filterable selects). */
  searchValue?: string;

  /**
   * Called as the user types in the mobile search box. When omitted, the mobile
   * sheet shows no search box — the desktop trigger handles filtering on its own.
   */
  onSearchChange?: (value: string) => void;

  /** Placeholder for the mobile search box. Default: "Search". */
  searchPlaceholder?: string;
}

const MOBILE_QUERY = '(max-width: 767px)';

const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"]):not([disabled])';

/** Focus the next tabbable element in the document after the given anchor. */
function focusNextTabbable(anchor: HTMLElement | null | undefined) {
  if (!anchor) return;
  const all = Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  const anchorInputs = Array.from(
    anchor.querySelectorAll<HTMLElement>('input, button, select, textarea')
  );
  const lastAnchorEl = anchorInputs[anchorInputs.length - 1] ?? anchor;
  const idx = all.indexOf(lastAnchorEl);
  if (idx >= 0 && idx < all.length - 1) {
    all[idx + 1].focus();
  }
}

/**
 * Focus the first option in the list, if present.
 *
 * ## Invariants:
 * - Focuses with `preventScroll: true`. Why: on open the menu's first positioning pass
 *   has no measured height, so the flip-above cannot fire yet and the dropdown is placed
 *   below its anchor (PB-8564). When the anchor sits at the bottom edge of the viewport
 *   the option is below the fold, and a plain `.focus()` makes the browser scroll the
 *   scroll container (`<main>`) to reveal it. That scroll is caught by the close-on-scroll
 *   handler and the menu self-closes within ~120ms before the next-frame flip can settle.
 *   Suppressing the focus scroll leaves the flip to position the menu in view and keeps a
 *   genuine user scroll (which still fires) closing it.
 */
function focusFirst(listRef: HTMLUListElement | null) {
  const first = listRef?.querySelector<HTMLElement>('[role="option"]');
  first?.focus({ preventScroll: true });
}

/** Move focus to the next option after `current`, if any. */
function focusNext(listRef: HTMLUListElement | null, current: HTMLElement) {
  const options = Array.from(listRef?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
  const i = options.indexOf(current);
  if (i < options.length - 1) options[i + 1]?.focus();
}

/** Move focus to the previous option before `current`, if any. */
function focusPrev(listRef: HTMLUListElement | null, current: HTMLElement) {
  const options = Array.from(listRef?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
  const i = options.indexOf(current);
  if (i > 0) options[i - 1]?.focus();
}

/** Focus the last option in the list, if present. */
function focusLast(listRef: HTMLUListElement | null) {
  const options = Array.from(listRef?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
  options[options.length - 1]?.focus();
}

/**
 * Predicate to check whether the currently active element is one of our options.
 * Used to decide how ArrowUp/ArrowDown should move focus.
 */
function isOptionInList(
  listRef: HTMLUListElement | null,
  el: HTMLElement | null
): el is HTMLElement {
  if (!el || !listRef) return false;
  return el.getAttribute('role') === 'option' && listRef.contains(el);
}

export function MenuList({
  open,
  onClose,
  options,
  onSelect,
  title,
  anchorRef,
  anchoredOnMobile = false,
  matchAnchorWidth = false,
  listboxId,
  filterable = false,
  noOptionsText = 'No data found',
  selectedValue,
  selectedValues,
  closeOnSelect = true,
  renderOption,
  headerSlot,
  menuZIndex,
  maxWidth,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search',
}: MenuListProps) {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  // Type-in-field comboboxes opt out of the mobile bottom sheet so suggestions stay anchored
  // under the active input (PB-2909); everything else keeps the bottom-sheet presentation.
  const useBottomSheet = isMobile && !anchoredOnMobile;
  const listRef = useRef<HTMLUListElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const showMobileSearch = filterable && onSearchChange != null;

  // Anchor the dropdown under the trigger and keep it glued there while open
  // (soft keyboard / scroll shifts, flip-above when space is short) — PB-2909.
  const dropdownStyle = useAnchoredDropdownPosition({
    open,
    disabled: useBottomSheet,
    anchorRef,
    dropdownRef,
    matchAnchorWidth,
    // Tied to anchoredOnMobile because that flag marks the type-in-field comboboxes
    // (Autocomplete is its only setter). Select / MultiSelect / MoreBtn keep the plain
    // flip-when-it-does-not-fit rule and stay uncapped.
    preferBelow: anchoredOnMobile,
  });

  // Lock body scroll while the mobile bottom sheet is open; restore afterwards.
  useEffect(() => {
    if (!open || !useBottomSheet) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, useBottomSheet]);

  // Manage initial focus when the menu opens:
  // - mobile → search box if present, else the close button.
  // - desktop → first option, unless filterable (trigger keeps focus for typing).
  useEffect(() => {
    if (!open) return;
    if (useBottomSheet) {
      if (showMobileSearch) {
        searchInputRef.current?.focus();
      } else {
        closeButtonRef.current?.focus();
      }
    } else if (!filterable) {
      // Small delay so portal is mounted
      const t = requestAnimationFrame(() => {
        focusFirst(listRef.current);
      });
      return () => cancelAnimationFrame(t);
    }
  }, [open, useBottomSheet, filterable, showMobileSearch]);

  // Close the anchored dropdown when clicking outside both anchor and dropdown.
  // (The invisible backdrop uses pointer-events: none, so only actual clicks count.)
  useEffect(() => {
    if (!open || useBottomSheet) return;
    const handleMouseDown = (e: Event) => {
      const target = e.target as Node;
      if (anchorRef?.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, useBottomSheet, onClose, anchorRef]);

  // Close the desktop dropdown when scrolling outside the dropdown. Kept desktop-only:
  // on mobile a scroll (e.g. the soft keyboard opening) must not dismiss the anchored
  // suggestions mid-type (PB-2909).
  useEffect(() => {
    if (!open || isMobile) return;
    const handleScroll = (e: Event) => {
      if (dropdownRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener('scroll', handleScroll, true);
    return () => document.removeEventListener('scroll', handleScroll, true);
  }, [open, isMobile, onClose]);

  /**
   * Keyboard support for the listbox:
   * - Escape       → close the menu.
   * - ArrowUp/Down → move focus between options.
   * - Home / End   → jump to first / last option.
   * - Enter/Space  → activate the focused option.
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLUListElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
        const active = document.activeElement as HTMLElement | null;
        if (e.key === 'Tab' && isOptionInList(listRef.current, active)) {
          const opts = Array.from(
            listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []
          );
          if (opts.indexOf(active) === opts.length - 1) {
            e.preventDefault();
            onClose();
            focusNextTabbable(anchorRef?.current);
            return;
          }
        }
        e.preventDefault();
        if (isOptionInList(listRef.current, active)) {
          focusNext(listRef.current, active);
        } else {
          focusFirst(listRef.current);
        }
        return;
      }
      if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
        e.preventDefault();
        const active = document.activeElement as HTMLElement | null;
        if (isOptionInList(listRef.current, active)) {
          const options = Array.from(
            listRef.current?.querySelectorAll<HTMLElement>('[role="option"]') ?? []
          );
          if (e.key === 'Tab' && options.indexOf(active) === 0) {
            anchorRef?.current?.querySelector<HTMLElement>('input')?.focus();
            return;
          }
          focusPrev(listRef.current, active);
        } else {
          focusLast(listRef.current);
        }
        return;
      }
      if (e.key === 'Home') {
        e.preventDefault();
        focusFirst(listRef.current);
        return;
      }
      if (e.key === 'End') {
        e.preventDefault();
        focusLast(listRef.current);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        const target = e.target as HTMLElement;
        if (target.getAttribute('role') === 'option') {
          e.preventDefault();
          const value = target.getAttribute('data-value');
          if (value != null) {
            onSelect(value);
            onClose();
          }
        }
      }
    },
    [onClose, onSelect]
  );

  /**
   * Convenience handler for individual option buttons (primarily for Enter/Space).
   * Click events already call `onSelect` + `onClose`; this mirrors that via keyboard.
   */
  const handleOptionKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, value: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(value);
        if (closeOnSelect) onClose();
      }
    },
    [closeOnSelect, onClose, onSelect]
  );

  const handleBackdropClick = useCallback(
    (e: MouseEvent) => {
      // PB-2612: The mobile sheet is portaled but still bubbles through the React tree to
      // Select's wrapper `onClick={handleOpen}`, which would immediately reopen the sheet.
      // Stop propagation so tap-outside actually dismisses the sheet on mobile.
      e.stopPropagation();
      onClose();
    },
    [onClose]
  );

  if (!open) return null;

  const listContent = (
    <>
      {headerSlot != null && <div className="tw:border-b tw:border-gray-light">{headerSlot}</div>}
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        tabIndex={-1}
        aria-label={title ?? 'Options'}
        onKeyDown={handleKeyDown}
        className={menuListClasses}
        data-cy="menu-list"
      >
        {options.length === 0 ? (
          <li role="presentation" className={menuListEmptyStateClasses}>
            {noOptionsText}
          </li>
        ) : (
          options.map((opt) => {
            const isSelected = selectedValues
              ? selectedValues.includes(opt.value)
              : selectedValue != null && opt.value === selectedValue;
            return (
              <li key={opt.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={opt.disabled || undefined}
                  disabled={opt.disabled}
                  title={opt.title}
                  data-value={opt.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (opt.disabled) return;
                    onSelect(opt.value);
                    if (closeOnSelect) onClose();
                  }}
                  onKeyDown={(e) => {
                    if (opt.disabled) return;
                    handleOptionKeyDown(e, opt.value);
                  }}
                  className={menuListOptionButtonClasses({ isSelected, disabled: opt.disabled })}
                >
                  {renderOption ? renderOption(opt, isSelected) : opt.label}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </>
  );

  let content: ReactNode;

  if (useBottomSheet) {
    content = (
      <>
        {/* Dimmed backdrop closes the dialog when clicked. */}
        <div
          className={menuListMobileBackdropClasses}
          aria-hidden
          data-cy="menu-list-backdrop"
          onClick={handleBackdropClick}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (stacking z-index from prop)
          style={menuZIndex != null ? { zIndex: menuZIndex - 1 } : undefined}
        />
        {/* Bottom sheet dialog used as the mobile presentation of the menu. */}
        <div
          className={menuListMobileDialogClasses}
          role="dialog"
          aria-modal="true"
          aria-label={title ?? 'Menu'}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (stacking z-index from prop)
          style={menuZIndex != null ? { zIndex: menuZIndex } : undefined}
        >
          {/* Header area with optional title and dedicated close button. */}
          <div className={menuListMobileHeaderClasses}>
            {title != null && <h2 className={menuListMobileTitleClasses}>{title}</h2>}
            <button
              ref={closeButtonRef}
              type="button"
              onClick={(e) => {
                // PB-2612: same portal-bubble guard as the backdrop and option buttons —
                // without stopPropagation the click reaches Select's wrapper handleOpen and
                // reopens the sheet, so the close (X) appears to do nothing on mobile.
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close"
              data-cy="menu-list-close"
              className={menuListMobileCloseButtonClasses}
            >
              <IoClose className="tw:h-5 tw:w-5" aria-hidden />
            </button>
          </div>
          {showMobileSearch && (
            // stopPropagation: PB-2612 portal-bubble guard so the click doesn't reach
            // Select's wrapper onClick and reopen the sheet.
            <div className={menuListMobileSearchClasses} onClick={(e) => e.stopPropagation()}>
              <Input
                ref={searchInputRef}
                value={searchValue ?? ''}
                onChange={(v) => onSearchChange?.(v)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                leftIcon={<MdSearch className="tw:h-5 tw:w-5 tw:text-gray-dark" aria-hidden />}
                hideBottomPadding
                data-cy="select-mobile-search"
              />
            </div>
          )}
          {/* Scrollable region that contains the listbox. */}
          <div className={menuListMobileBodyClasses}>{listContent}</div>
        </div>
      </>
    );
  } else {
    content = (
      <>
        {/* Invisible overlay establishing a stacking context behind the dropdown. */}
        <div
          className={menuListDesktopOverlayClasses}
          aria-hidden
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (stacking z-index from prop)
          style={menuZIndex != null ? { zIndex: menuZIndex - 1 } : undefined}
        />
        {/* Desktop dropdown, positioned relative to the trigger via `dropdownStyle`. */}
        <div
          ref={dropdownRef}
          className={menuListDesktopDropdownClasses}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic runtime value (computed dropdown position/z-index/width)
          style={{
            // Load-bearing, not decoration: this menu is portalled to <body> and positioned
            // with viewport coordinates from getBoundingClientRect. Drop `fixed` and the
            // element is static, every offset below is ignored, and the menu renders at the
            // end of the document instead of under its trigger (PB-7432).
            position: 'fixed',
            ...(menuZIndex != null && { zIndex: menuZIndex }),
            top: dropdownStyle.top,
            bottom: dropdownStyle.bottom,
            left: dropdownStyle.left,
            right: dropdownStyle.right,
            minWidth: dropdownStyle.minWidth,
            width: dropdownStyle.width,
            maxHeight: dropdownStyle.maxHeight,
            ...(maxWidth && { maxWidth }),
          }}
        >
          {listContent}
        </div>
      </>
    );
  }

  return createPortal(content, document.body);
}
